<script lang="ts">
  interface Props {
    values: number[];
    label: string;
    labels?: string[];
    formatValue?: (value: number) => string;
    width?: number;
    height?: number;
    area?: boolean;
    summary?: boolean;
  }

  const number = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 1 });

  let {
    values,
    label,
    labels = [],
    formatValue = (value: number) => number.format(value),
    width = 160,
    height = 40,
    area = true,
    summary = true,
  }: Props = $props();

  // Room for the 4px end dot and its 2px surface ring.
  const PAD = 6;

  let active: number | null = $state(null);

  const clean = $derived(values.map((value) => (Number.isFinite(value) ? value : 0)));
  const min = $derived(Math.min(0, ...clean));
  const max = $derived(Math.max(0, ...clean));

  function yFor(value: number): number {
    if (max === min) return height - PAD;
    return PAD + ((max - value) / (max - min)) * (height - 2 * PAD);
  }

  const points = $derived(
    clean.map((value, index) => ({
      x: clean.length === 1 ? width / 2 : PAD + (index * (width - 2 * PAD)) / (clean.length - 1),
      y: yFor(value),
      value,
      label: labels[index],
    }))
  );
  const zeroY = $derived(yFor(0));
  const line = $derived(points.map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x},${point.y}`).join(' '));
  const fill = $derived(
    points.length > 1 ? `${line} L${points[points.length - 1].x},${zeroY} L${points[0].x},${zeroY} Z` : ''
  );

  const accessibleName = $derived(
    points.length === 0
      ? `${label}: no data`
      : `${label}: ${points.map((point) => (point.label ? `${point.label} ${formatValue(point.value)}` : formatValue(point.value))).join(', ')}`
  );

  const latest = $derived(points[points.length - 1]);
  const previous = $derived(points.length > 1 ? points[points.length - 2] : undefined);
  const change = $derived.by(() => {
    if (!latest || !previous) return null;
    const delta = latest.value - previous.value;
    const against = previous.label || 'previous';
    if (delta === 0) return `no change from ${against}`;
    // U+2212, not a hyphen: it's the typographic minus and screen readers say "minus".
    return `${delta > 0 ? '+' : '\u2212'}${formatValue(Math.abs(delta))} vs ${against}`;
  });

  function track(event: PointerEvent) {
    if (points.length === 0) return;
    const box = (event.currentTarget as SVGSVGElement).getBoundingClientRect();
    const x = ((event.clientX - box.left) / box.width) * width;
    let nearest = 0;
    for (let index = 1; index < points.length; index += 1) {
      if (Math.abs(points[index].x - x) < Math.abs(points[nearest].x - x)) nearest = index;
    }
    active = nearest;
  }
</script>

<div class="ws-sparkline">
  <div class="ws-sparkline__plot" role="img" aria-label={accessibleName} style="width: {width}px; height: {height}px">
    <svg
      {width}
      {height}
      viewBox="0 0 {width} {height}"
      aria-hidden="true"
      onpointermove={track}
      onpointerleave={() => (active = null)}
    >
      <line class="ws-sparkline__baseline" x1={PAD} x2={width - PAD} y1={zeroY + 0.5} y2={zeroY + 0.5} />
      {#if area && fill}<path class="ws-sparkline__area" d={fill} />{/if}
      {#if points.length > 1}<path class="ws-sparkline__line" d={line} />{/if}
      {#if active !== null && points[active]}
        <line
          class="ws-sparkline__guide"
          x1={points[active].x}
          x2={points[active].x}
          y1={PAD / 2}
          y2={height - PAD / 2}
        />
        <circle class="ws-sparkline__dot" cx={points[active].x} cy={points[active].y} r="4" />
      {:else if latest}
        <circle class="ws-sparkline__dot" cx={latest.x} cy={latest.y} r="4" />
      {/if}
    </svg>
    {#if active !== null && points[active]}
      <span class="ws-sparkline__tip" style="left: {points[active].x}px" aria-hidden="true">
        {formatValue(points[active].value)}
        {#if points[active].label}<span>{points[active].label}</span>{/if}
      </span>
    {/if}
  </div>
  {#if summary}
    <p class="ws-sparkline__summary">
      {#if !latest}
        No data yet
      {:else if latest.label}
        <strong>{formatValue(latest.value)}</strong> in {latest.label}{change ? ` · ${change}` : ''}
      {:else}
        Latest <strong>{formatValue(latest.value)}</strong>{change ? ` · ${change}` : ''}
      {/if}
    </p>
  {/if}
</div>
